import type { CSSProperties } from 'react';
import './GreenCtaButton.css';
import flashImg from '../button/flash.png';

export type GreenCtaButtonProps = {
  /** Label image (preferred when Figma export exists). */
  textSrc?: string;
  /** Plain-text label when no Figma export is available. */
  labelText?: string;
  ariaLabel: string;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
  /** Absolute left position in design px. */
  left: number;
  /** Button width in design px. */
  width: number;
  /** Absolute top (default 1682). */
  top?: number;
  /** Height (default 173). */
  height?: number;
  /** Radial gradient ellipse size + position. */
  gradient?: { sizeX: number; sizeY: number; atX: number; atY: number };
  /** Flash glint position relative to button. */
  flash?: { left: number; top: number };
  /** Label image size (ignored when using labelText). */
  textSize?: { width: number; height: number };
};

/** Shared green primary CTA (border #005854, bevel, flash glint). */
export function GreenCtaButton({
  textSrc,
  labelText,
  ariaLabel,
  className,
  onClick,
  disabled = false,
  left,
  width,
  top = 1682,
  height = 173,
  gradient = {
    sizeX: 460.08,
    sizeY: 977.58,
    atX: width / 2,
    atY: 86.5,
  },
  flash = { left: width - 25.605, top: 23 },
  textSize = { width: 404, height: 105 },
}: GreenCtaButtonProps) {
  const style = {
    '--cta-left': `${left}px`,
    '--cta-top': `${top}px`,
    '--cta-width': `${width}px`,
    '--cta-height': `${height}px`,
    '--cta-gradient': `${gradient.sizeX}px ${gradient.sizeY}px at ${gradient.atX}px ${gradient.atY}px`,
    '--cta-flash-left': `${flash.left}px`,
    '--cta-flash-top': `${flash.top}px`,
    '--cta-text-width': `${textSize.width}px`,
    '--cta-text-height': `${textSize.height}px`,
  } as CSSProperties;

  return (
    <button
      type="button"
      className={['green-cta-button', className].filter(Boolean).join(' ')}
      style={style}
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
    >
      <img
        className="green-cta-button__flash"
        src={flashImg}
        alt=""
        draggable={false}
      />
      {textSrc != null ? (
        <img
          className="green-cta-button__text"
          src={textSrc}
          alt=""
          draggable={false}
        />
      ) : (
        <span className="green-cta-button__label">{labelText}</span>
      )}
    </button>
  );
}
